import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Check, CircleNotch, CreditCard } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import CommissionBilling from "@/components/CommissionBilling";
import CreditsWidget from "@/components/CreditsWidget";
import AddonServices from "@/components/AddonServices";

export default function Billing() {
  const { user, refresh } = useAuth();
  const { t } = useTranslation();
  const [plans, setPlans] = useState({});
  const [loading, setLoading] = useState(null);
  const [params, setParams] = useSearchParams();
  const [polling, setPolling] = useState(false);

  useEffect(() => { api.get("/plans").then((r) => setPlans(r.data)).catch(() => {}); }, []);

  const poll = useCallback(async (sessionId, attempts = 0) => {
    if (attempts > 6) { setPolling(false); toast.error(t("billing.payNotConfirmed")); return; }
    try {
      const { data } = await api.get(`/billing/status/${sessionId}`);
      if (data.payment_status === "paid") { setPolling(false); toast.success(t("billing.subActivated")); refresh(); setParams({}); return; }
      if (data.status === "expired") { setPolling(false); toast.error(t("billing.sessionExpired")); return; }
      setTimeout(() => poll(sessionId, attempts + 1), 2000);
    } catch { setTimeout(() => poll(sessionId, attempts + 1), 2000); }
  }, [refresh, setParams]);

  useEffect(() => {
    const sid = params.get("session_id");
    if (sid) { setPolling(true); poll(sid); }
  }, [params, poll]);

  const subscribe = async (planId) => {
    setLoading(planId);
    try {
      const { data } = await api.post("/billing/checkout", { plan_id: planId, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) { toast.error(apiError(err)); setLoading(null); }
  };

  const sub = user?.subscription || {};

  return (
    <div className="p-8 max-w-5xl">
      <h1 className="text-3xl font-bold tracking-tight">{t("billing.title")}</h1>
      <p className="text-neutral-400 mt-1 mb-8">{t("billing.subtitle")}</p>

      <CommissionBilling />

      <CreditsWidget />

      <AddonServices />

      {polling && (
        <div className="flex items-center gap-3 bg-gold/10 border border-gold/30 rounded-xl p-4 mb-6">
          <CircleNotch className="animate-spin text-gold" /> <span className="text-sm">{t("billing.confirming")}</span>
        </div>
      )}

      <div className="border border-white/10 rounded-xl p-5 bg-[#121212] mb-8 flex items-center gap-4">
        <div className={`w-11 h-11 rounded-full flex items-center justify-center ${sub.status === "active" ? "bg-gold/20 text-gold" : "bg-white/10 text-neutral-400"}`}><CreditCard size={22} /></div>
        <div>
          <div className="text-sm text-neutral-400">{t("billing.currentPlan")}</div>
          <div className="font-semibold text-lg capitalize">{sub.status === "active" ? t("billing.active", { plan: sub.plan }) : t("billing.noSub")}</div>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4" data-testid="billing-plans">
        {Object.entries(plans).map(([id, p]) => {
          const current = sub.status === "active" && sub.plan === id;
          return (
            <div key={id} className={`rounded-xl p-6 border ${id === "pro" ? "border-gold/40 bg-[#151208]" : "border-white/10 bg-[#121212]"}`} data-testid={`billing-plan-${id}`}>
              <h3 className="font-serif text-2xl">{p.name}</h3>
              <div className="mt-3 flex items-end gap-1"><span className="text-4xl font-bold">${p.price}</span><span className="text-neutral-500 text-sm mb-1">{t("billing.perMonth")}</span></div>
              <ul className="mt-5 space-y-2">{p.features.map((f, i) => <li key={i} className="flex gap-2 text-sm text-neutral-300"><Check size={16} className="text-gold shrink-0 mt-0.5" weight="bold" />{f}</li>)}</ul>
              <Button disabled={loading === id || current} onClick={() => subscribe(id)} data-testid={`subscribe-${id}`}
                className={`w-full mt-6 rounded-full ${current ? "bg-white/10 text-neutral-400" : "bg-gold text-obsidian hover:bg-gold/90 font-semibold"}`}>
                {current ? t("billing.current") : loading === id ? <CircleNotch className="animate-spin" /> : t("billing.subscribe")}
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
